import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BOT_LETTER_AVATAR_FONT_CLASS, getBotLetterAvatarTileStyle } from "../../utils/botAvatarPlaceholders";
import { getProfileLetterAvatarColor, getProfileLetterAvatarLetter } from "../../utils/profileLetterAvatar";
import { isUsableAvatarUrl } from "../../utils/sessionUser";
import { AvatarWithXpCrown, normalizeXpWeekCrown, xpWeekCrownReason, type XpWeekCrown } from "../Xp/AvatarWithXpCrown";
import { resolveAvatarFrameSrc } from "./avatarFrame";

export type UserAvatarSize = "hero" | "chip" | "md" | "sm" | "xs" | "tiny" | "lg" | "compact" | "micro" | "icon16" | "mini" | "fill";

const SIZE_CONFIG: Record<
  UserAvatarSize,
  { box: string; letter: string; shadow: string; crownSize?: "hero" | "sm" }
> = {
  hero: {
    box: "h-24 w-24 shrink-0",
    letter: "text-[34px]",
    shadow: "shadow-[0_8px_28px_rgba(8,2,18,0.45)]",
    crownSize: "hero",
  },
  chip: {
    box: "h-11 w-11 shrink-0",
    letter: "text-[20px]",
    shadow: "shadow-[0_2px_10px_rgba(0,0,0,0.3)] transition-shadow duration-200 group-hover:shadow-[0_4px_14px_rgba(0,0,0,0.4)]",
    crownSize: "sm",
  },
  md: {
    box: "h-10 w-10 shrink-0",
    letter: "text-[16px]",
    shadow: "",
    crownSize: "sm",
  },
  sm: {
    box: "h-[30px] w-[30px] shrink-0",
    letter: "text-[14px]",
    shadow: "",
    crownSize: "sm",
  },
  xs: {
    box: "h-8 w-8 shrink-0",
    letter: "text-[13px]",
    shadow: "",
    crownSize: "sm",
  },
  tiny: {
    box: "h-[22px] w-[22px] shrink-0",
    letter: "text-[10px]",
    shadow: "",
    crownSize: "sm",
  },
  lg: {
    box: "h-16 w-16 shrink-0",
    letter: "text-[28px]",
    shadow: "",
    crownSize: "sm",
  },
  compact: {
    box: "h-7 w-7 shrink-0",
    letter: "text-[12px]",
    shadow: "",
    crownSize: "sm",
  },
  micro: {
    box: "h-[14px] w-[14px] shrink-0",
    letter: "text-[8px]",
    shadow: "",
    crownSize: "sm",
  },
  icon16: {
    box: "h-4 w-4 shrink-0",
    letter: "text-[9px]",
    shadow: "",
    crownSize: "sm",
  },
  mini: {
    box: "h-[18px] w-[18px] shrink-0",
    letter: "text-[9px]",
    shadow: "",
    crownSize: "sm",
  },
  fill: {
    box: "h-full w-full",
    letter: "text-[14px]",
    shadow: "",
  },
};

export type UserAvatarProps = {
  src?: string | null;
  displayName?: string | null;
  avatarColor?: string | null;
  firstLetter?: string | null;
  syntheticBotLetterTile?: boolean;
  xpWeekCrown?: XpWeekCrown | string | null;
  /** Admin-granted avatar frame (`avatar_frame_url`); small sizes use `avatarFrameSmallUrl` when set. */
  avatarFrameUrl?: string | null;
  avatarFrameSmallUrl?: string | null;
  size?: UserAvatarSize;
  className?: string;
  imgClassName?: string;
  letterClassName?: string;
  alt?: string;
  loading?: "lazy" | "eager";
  /** When false, caller wraps {@link AvatarWithXpCrown} (e.g. activity stream row shell). */
  withCrown?: boolean;
};

function useFinePointerHover(): boolean {
  const [fineHover, setFineHover] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(hover: hover) and (pointer: fine)");
    const sync = () => setFineHover(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return fineHover;
}

function resolveLetter(displayName?: string | null, firstLetter?: string | null): string {
  if (firstLetter != null && String(firstLetter).trim() !== "") {
    return String(firstLetter).trim().charAt(0).toUpperCase();
  }
  return getProfileLetterAvatarLetter(displayName) || "?";
}

function resolveLetterTileStyle(
  displayName: string | null | undefined,
  avatarColor: string | null | undefined,
  syntheticBotLetterTile: boolean,
) {
  if (syntheticBotLetterTile) {
    return getBotLetterAvatarTileStyle({ avatar_color: avatarColor, name: displayName });
  }
  return {
    backgroundColor: avatarColor?.trim() || getProfileLetterAvatarColor(displayName ?? ""),
    color: "#1B062F",
  };
}

export function UserAvatar({
  src,
  displayName,
  avatarColor,
  firstLetter,
  syntheticBotLetterTile = false,
  xpWeekCrown = null,
  avatarFrameUrl = null,
  avatarFrameSmallUrl = null,
  size = "chip",
  className = "",
  imgClassName = "",
  letterClassName = "",
  alt = "",
  loading = "lazy",
  withCrown = true,
}: UserAvatarProps) {
  const config = SIZE_CONFIG[size];
  const resolvedSrc = isUsableAvatarUrl(src) ? String(src).trim() : "";
  const [failed, setFailed] = useState(false);
  const [photoRevealed, setPhotoRevealed] = useState(false);
  const [photoHovered, setPhotoHovered] = useState(false);
  const finePointerHover = useFinePointerHover();
  const heroCrown = size === "hero" ? normalizeXpWeekCrown(xpWeekCrown) : null;
  const frameSrc = resolveAvatarFrameSrc(size, avatarFrameUrl, avatarFrameSmallUrl);
  const showPhoto = Boolean(heroCrown) && (finePointerHover ? photoHovered : photoRevealed);

  useEffect(() => {
    setFailed(false);
  }, [resolvedSrc]);

  useEffect(() => {
    setPhotoRevealed(false);
  }, [xpWeekCrown]);

  const letter = resolveLetter(displayName, firstLetter);
  const letterTileStyle = resolveLetterTileStyle(displayName, avatarColor, syntheticBotLetterTile);
  const showLetter = !resolvedSrc || failed;
  const botLetterClass =
    syntheticBotLetterTile && size === "hero" ? "text-[40px] sm:text-[42px]" : config.letter;

  const letterNode = (
    <div
      className={`flex items-center justify-center ${config.box} ${BOT_LETTER_AVATAR_FONT_CLASS} ${config.shadow} ${botLetterClass} ${letterClassName} ${className}`}
      style={letterTileStyle}
      aria-hidden={alt === ""}
    >
      {letter}
    </div>
  );

  const imgNode = (
    <img
      src={resolvedSrc}
      alt={alt}
      className={`object-cover object-center ${config.box} ${config.shadow} ${imgClassName || className}`}
      loading={loading}
      decoding="async"
      onError={() => setFailed(true)}
    />
  );

  const inner = showLetter ? letterNode : imgNode;

  if (!withCrown) {
    return inner;
  }

  const crowned = (
    <AvatarWithXpCrown
      crown={showPhoto ? null : xpWeekCrown}
      frameSrc={frameSrc}
      size={config.crownSize}
      className={size === "fill" ? "block h-full w-full" : undefined}
    >
      {inner}
    </AvatarWithXpCrown>
  );

  if (!heroCrown) {
    return crowned;
  }

  const hint = showPhoto ? "Show crown" : finePointerHover ? "Hover to view" : "Tap to view";
  const crownReason = xpWeekCrownReason(heroCrown);

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => {
          if (finePointerHover) return;
          setPhotoRevealed((revealed) => !revealed);
        }}
        onPointerEnter={(event) => {
          if (finePointerHover && event.pointerType === "mouse") setPhotoHovered(true);
        }}
        onPointerLeave={() => setPhotoHovered(false)}
        aria-pressed={showPhoto}
        aria-label={showPhoto ? "Show weekly crown" : "Show profile photo"}
        className={`relative border-0 bg-transparent p-0 ${finePointerHover ? "cursor-default" : "cursor-pointer"}`}
      >
        {crowned}
        {finePointerHover && photoHovered ? null : (
          <span className="pointer-events-none absolute bottom-1.5 left-1/2 z-[4] -translate-x-1/2 whitespace-nowrap bg-[#D2FF00] px-1.5 py-[3px] font-anton text-[9px] font-normal uppercase leading-none tracking-wide text-black shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
            {hint}
          </span>
        )}
      </button>
      {showPhoto || !crownReason ? null : (
        <span className="absolute left-[calc(100%+0.6rem)] top-7 z-[5] flex w-max max-w-[6.75rem] flex-col items-start gap-1.5 text-left">
          <span className="font-chakra text-[11px] font-normal normal-case leading-[14px] text-white/80">
            <span className="text-white">{crownReason.place}</span>
            {" in XP leaderboard last week"}
          </span>
          <Link
            to="/xp-leaderboard"
            aria-label="See this week's XP leaderboard"
            className="inline-flex items-center gap-1 whitespace-nowrap font-anton text-[11px] font-normal uppercase leading-none tracking-wide text-white no-underline hover:text-[#D2FF00]"
          >
            See this week
            <svg viewBox="0 0 256 256" className="h-3 w-3 shrink-0" aria-hidden>
              <path
                fill="currentColor"
                d="M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z"
              />
            </svg>
          </Link>
        </span>
      )}
    </span>
  );
}
